import React, { useState, useEffect } from 'react';
import {
  FileText,
  Sparkles,
  Check,
  Copy,
  ChevronDown,
  ChevronUp,
  Download,
  Edit3,
  RefreshCw,
  AlertCircle,
  ArrowRight,
  Globe,
  MapPin,
  Target,
  CheckCircle2,
  HelpCircle,
  Info,
  FileCode
} from 'lucide-react';
import { ContentPlanningProject, ContentPlanningPage, PageFinalOutline } from '../../types';
import { ContentPlanningPdfService } from '../../services/contentPlanningPdfService';

interface PageContentGenerationWorkstationProps {
  project: ContentPlanningProject;
  onSaveProject: (project: ContentPlanningProject) => void;
  onGoToStep3?: () => void;
}

export const PageContentGenerationWorkstation: React.FC<PageContentGenerationWorkstationProps> = ({
  project,
  onSaveProject,
  onGoToStep3,
}) => {
  const pages = project.pages || [];
  const [selectedPageId, setSelectedPageId] = useState<string>(pages[0]?.id || '');
  const [isOutlineExpanded, setIsOutlineExpanded] = useState<boolean>(false);
  const [isSeoPanelExpanded, setIsSeoPanelExpanded] = useState<boolean>(false);
  const [isFaqsExpanded, setIsFaqsExpanded] = useState<boolean>(false);
  const [isInputsExpanded, setIsInputsExpanded] = useState<boolean>(false);
  const [isRawOutputExpanded, setIsRawOutputExpanded] = useState<boolean>(false);

  // Generation state
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationStep, setGenerationStep] = useState<number>(0);
  const [errorState, setErrorState] = useState<string | null>(null);

  // Editing state
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editedContentText, setEditedContentText] = useState<string>('');
  const [copiedContent, setCopiedContent] = useState<boolean>(false);
  const [copiedPrompt, setCopiedPrompt] = useState<boolean>(false);

  // Custom prompt paste modal/area
  const [pastedArticleText, setPastedArticleText] = useState<string>('');
  const [showPromptBox, setShowPromptBox] = useState<boolean>(false);

  // Sync selected page ID if initial empty
  useEffect(() => {
    if (!selectedPageId && pages.length > 0) {
      setSelectedPageId(pages[0].id);
    }
  }, [pages, selectedPageId]);

  const selectedPage = pages.find(p => p.id === selectedPageId) || pages[0];

  // Sync editedContentText when selected page changes
  useEffect(() => {
    if (selectedPage) {
      setEditedContentText(selectedPage.generatedContent || '');
      setIsEditing(false);
      setErrorState(null);
    }
  }, [selectedPageId, selectedPage?.generatedContent]);

  if (!selectedPage) {
    return (
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '48px', textAlign: 'center' }}>
        <FileText size={42} color="#334155" style={{ margin: '0 auto 16px auto' }} />
        <h3 style={{ fontSize: '18px', color: '#ffffff', fontWeight: 700, margin: '0 0 8px 0' }}>
          No Pages Planned in this Project
        </h3>
        <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
          Please go back to Step 1 and add pages to your content plan first.
        </p>
      </div>
    );
  }

  const outline: PageFinalOutline | undefined = selectedPage.finalOutline;
  const compHeadingsList = selectedPage.competitorHeadings || [];
  const painPointsList = selectedPage.painPoints || [];

  // Helper for updating currently selected page strictly isolated by ID (Req 17)
  const updateCurrentPage = (updatedPageFields: Partial<ContentPlanningPage>) => {
    const updatedPages = pages.map(p => {
      if (p.id === selectedPage.id) {
        return {
          ...p,
          ...updatedPageFields,
        };
      }
      return p;
    });

    onSaveProject({
      ...project,
      pages: updatedPages,
      updatedAt: new Date().toISOString(),
    });
  };

  const formattedCompetitorData = compHeadingsList.length === 0
    ? 'No competitor headings collected yet.'
    : compHeadingsList
        .filter(c => (c.headings && c.headings.length > 0) || c.nameOrUrl)
        .map(c => `Competitor #${c.competitorNumber} (${c.nameOrUrl || 'URL not specified'}):\n` + (c.headings || []).map(h => `  - ${h}`).join('\n'))
        .join('\n\n');

  const combinedHeadingsData = selectedPage.combinedCompetitorHeadings?.join('\n') || (
    compHeadingsList
      .flatMap(c => c.headings || [])
      .filter(Boolean)
      .map(h => `- ${h}`)
      .join('\n') || 'None consolidated yet.'
  );

  const formattedPainPointsData = painPointsList.length === 0
    ? 'No customer pain points recorded yet.'
    : painPointsList.map(p => `- ${p.title}${p.description ? `: ${p.description}` : ''}`).join('\n');

  const [promptTab, setPromptTab] = useState<'setup' | 'generation'>('setup');
  const [copiedSetupPrompt, setCopiedSetupPrompt] = useState<boolean>(false);

  // Service Page Content Setup Prompt builder
  const servicePageContentSetupPromptText = `Act as a senior SEO content strategist and content workflow assistant.

The website already contains multiple service pages.

Your task is to organize the service pages and prepare the required content inputs for each page before the actual content-generation stage.

## OBJECTIVE

Display the confirmed service pages clearly and allow the user to provide or review the information required to generate the final content for each service page.

This step is a content preparation step only.

DO NOT write the complete service-page content.

DO NOT generate paragraphs for the final webpage.

DO NOT create a new SEO strategy.

DO NOT redesign the approved Step 3 outline.

---

# 1. SERVICE PAGE LIST

Use the confirmed services provided in the project information.

For each service, display:

* Service name: ${selectedPage.pageName}
* Target location: ${project.targetedLocation || 'Not specified'}
* Primary keyword: ${selectedPage.primaryKeyword || 'Not specified'}
* Page URL / suggested URL: ${selectedPage.targetUrl}
* Page status: ${selectedPage.status || 'planned'}
* Content status: ${selectedPage.generatedContent ? 'completed' : 'pending'}
* Step 3 strategy/outline status: ${outline ? 'approved' : 'pending'}

Each service should be treated as an individual service-page content project.

---

# 2. SERVICE PAGE CONTENT INPUTS

For the selected service page, collect and display the following:

### Basic Page Information

* Business name: ${project.clientName}
* Service name: ${selectedPage.pageName}
* Primary location: ${project.targetedLocation || 'Not specified'}
* Service areas: ${project.targetedLocation || 'Not specified'}
* Page URL: ${selectedPage.targetUrl}
* Primary keyword: ${selectedPage.primaryKeyword || 'Not specified'}
* Secondary keywords: ${selectedPage.secondaryKeyword || 'Not specified'}
* Long-tail keywords: ${selectedPage.secondaryKeywordVariations?.join(', ') || 'None'}
* Relevant entities: None

### Business Information

Use only verified information supplied by the user or approved research.

Collect:

* Services included: ${selectedPage.pageName}
* Service limitations: [INFORMATION REQUIRED]
* Unique selling points: None logged
* Experience: [INFORMATION REQUIRED]
* Qualifications: [INFORMATION REQUIRED]
* Certifications: [INFORMATION REQUIRED]
* Licensing: [INFORMATION REQUIRED]
* Insurance: [INFORMATION REQUIRED]
* Guarantees: [INFORMATION REQUIRED]
* Relevant associations: [INFORMATION REQUIRED]
* Business differentiators: ${project.category || 'Not specified'}

Any missing information must remain blank or be marked:

[INFORMATION REQUIRED]

Never invent missing business information.

---

# 3. CUSTOMER INFORMATION

Collect the customer information relevant to this specific service.

Include:

* Primary customer problem: ${painPointsList[0]?.title || 'Customer service need'}
* Common customer concerns: ${painPointsList.map(p => p.title).join('; ') || 'Service quality, pricing, dependability'}
* Customer pain points: ${formattedPainPointsData}
* Customer objections: Cost, reliability, process transparency
* Common questions: ${(outline?.aeoFaqs || []).map(f => f.question).join('; ') || 'Service timeline, pricing factors'}
* Buying considerations: Quality, experience, location
* Desired outcome: High quality, long-lasting solution
* Reasons customers choose this service: Expert execution, local presence
* Reasons customers hesitate to purchase: Unclear pricing, distrust

---

# 4. LOCAL INFORMATION

Collect only verified local information relevant to the service.

Include:

* Target city: ${project.targetedLocation || 'Not specified'}
* Relevant suburbs/neighborhoods: ${project.targetedLocation || 'Not specified'}
* Surrounding service areas: ${project.targetedLocation || 'Not specified'}
* Local conditions: General regional market conditions
* Seasonal considerations: Standard seasonal patterns
* Local regulations: [INFORMATION REQUIRED]
* Local property/building characteristics: [INFORMATION REQUIRED]
* Relevant local entities: ${project.targetedLocation || 'None'}

Do not add locations simply for keyword insertion.

---

# 5. APPROVED STEP 3 STRATEGY

Attach the approved Step 3 content strategy and outline for the selected service.

The Step 3 outline is the source of truth for page structure.

The content-generation stage must follow the approved strategy unless the user explicitly changes it.

SEO Title: ${outline?.seoTitle || 'Not set'}
Meta Description: ${outline?.metaDescription || 'Not set'}
H1: ${outline?.h1 || selectedPage.pageName}
Recommended Word Count: ~${outline?.recommendedWordCount || 2500} words

Approved Outline Sections:
${(outline?.outlineSections || []).map((sec, i) => `${i + 1}. [${sec.headingTag}] ${sec.headingText}\n   Purpose: ${sec.purpose}\n   Key Points: ${sec.keyPoints.join('; ')}`).join('\n\n')}

Content Gaps:
${(outline?.contentGaps || []).map(g => `- ${g}`).join('\n') || 'None specified.'}

Value Additions:
${(outline?.valueAdditions || []).map(v => `- ${v}`).join('\n') || 'None specified.'}

---

# 6. USER CONTENT / BUSINESS NOTES

Additional Business Notes & Instructions:
${selectedPage.notes || 'None logged yet.'}

---

# 7. CONTENT READINESS CHECK

### Ready
- Primary Keyword & Variations (${selectedPage.primaryKeyword || 'Set'})
- Target Location (${project.targetedLocation || 'Set'})
- Approved Step 3 Strategy (${outline ? 'Approved' : 'Pending'})
- Customer Pain Points (${painPointsList.length} logged)

### Missing
- Specific Business Licenses & Certifications [INFORMATION REQUIRED]
- Fixed Pricing Schedule [INFORMATION REQUIRED]

### Requires Verification
- Specific Years of Experience Claims [REQUIRES VERIFICATION]
- Custom Guarantees [REQUIRES VERIFICATION]

---

# 8. OUTPUT BRIEF

Service Page: ${selectedPage.pageName}
Location: ${project.targetedLocation || 'Not specified'}
Primary Keyword: ${selectedPage.primaryKeyword || 'Not specified'}
Page URL: ${selectedPage.targetUrl}
Content Status: ${outline ? 'Ready for Step 4B Generation' : 'Needs Step 3 Strategy'}

---

# CRITICAL RULE

This step prepares the service page for writing.

It does NOT write the final webpage.

Once the service-page information and approved Step 3 outline are ready, they will be passed to Step 4B: Service Page Content Generation.
`;

  const isHomepage = selectedPage.pageType === 'Homepage' || selectedPage.pageName.toLowerCase().includes('homepage');

  // Default Content Generation Prompt for Homepage / generic pages (Step 4B Master Prompt)
  const defaultGenerationPromptText = `Act as a senior SEO content strategist and conversion-focused content writer. 

Your task is to generate the complete webpage content for the selected page using the research, keyword architecture, competitor research, customer insights, and approved content strategy provided below. 

Very Important type of content has to be in a way that we can make section for webpage and should be targeted conversions not only informational and article format. Shall Have Proper CTAs For each section

Step 3 has already analyzed the research and created the approved content strategy and outline.

Your job in Step 4 is to turn that approved strategy into the actual webpage content.

DO NOT redesign the strategy.
DO NOT independently create a different outline.
DO NOT ignore the approved outline.
DO NOT repeat the research process.
DO NOT explain your reasoning.
DO NOT write a new strategy.

Follow the approved Step 3 outline as the structural blueprint for the final webpage.


==================================================
PRIMARY OBJECTIVE
==================================================

Create comprehensive, useful, human-feeling webpage content that:

- Satisfies the primary search intent
- Addresses relevant customer needs and pain points
- Follows the approved Step 3 outline
- Covers the content gaps identified in Step 3
- Incorporates relevant value additions identified in Step 3
- Uses keywords naturally
- Uses relevant semantic entities naturally
- Provides useful local relevance where supported
- Communicates legitimate trust and credibility where supported
- Answers relevant customer questions
- Provides practical information where appropriate
- Avoids unnecessary fluff
- Is easy to read
- Is factually responsible
- Supports the user's decision-making process
- Provides an appropriate next step or CTA where relevant


==================================================
STEP 3 IS THE APPROVED CONTENT BLUEPRINT
==================================================

Step 3 has already determined the recommended structure of the page.

Use the following Step 3 output as the approved blueprint:

- SEO Title
- Meta Description
- H1
- Recommended Word Count
- Outline Sections
- Content Gaps
- Value Additions
- FAQs

Follow the approved outlineSections in the order provided.

Respect the headingTag assigned to each section.

Do not independently restructure the page.

Do not add unrelated sections simply because they are common in other SEO content.

Do not remove important approved sections unless the available information makes the section impossible to write accurately.

If an approved section requires information that has not been supplied, do not fabricate it.


==================================================
CONTENT DEPTH
==================================================

Write comprehensive content appropriate to the topic and page type.

Where the page strategy calls for substantial long-form coverage, aim for approximately 2,500+ words where appropriate.

The recommended word count from Step 3 should guide the depth of the content.

Do not inflate the article with filler simply to reach a word count.

Useful and relevant information is more important than arbitrary length.


==================================================
HEADING HIERARCHY
==================================================

The heading hierarchy established by Step 3 is approved and must be preserved.

Use actual Markdown heading syntax:

# = H1
## = H2
### = H3

For example:

# Auto Parts in Trinidad and Tobago

Introductory content...

## Find the Right Auto Parts for Your Vehicle

Supporting content...

### Parts for Cars, SUVs, and Trucks

Supporting content...

### How to Confirm Part Compatibility

Supporting content...

## Why Choose the Right Auto Parts Supplier

Supporting content...


HEADING RULES:

- Use one primary H1 for the page.
- Use the Step 3 H1 as the page's H1.
- Use H2 for sections marked H2 in Step 3.
- Use H3 for sections marked H3 in Step 3.
- Preserve the order of the approved outline.
- Do not convert headings into bold text.
- Do not write headings as plain text labels.
- Do not add H4, H5, or H6 unless explicitly required by the approved Step 3 outline.
- Do not independently change an H2 into an H3 or an H3 into an H2.
- Do not create additional headings merely to increase keyword coverage.

The final output must make the H1, H2, and H3 hierarchy clear through Markdown heading syntax.


==================================================
COMPETITOR RESEARCH
==================================================

Use the supplied competitor research as background research.

Competitor headings and identified gaps should inform the content coverage.

Do not:

- Copy competitor content
- Copy competitor wording
- Copy competitor headings unnecessarily
- Reproduce a competitor's structure independently
- Mention competitors unless specifically required and supported
- Claim that the page will outrank competitors

Use competitor research to ensure that important topics are covered and relevant gaps identified in Step 3 are addressed.


==================================================
KEYWORD USE
==================================================

Use the Primary / Seed Keyword naturally and prominently where appropriate.

Use Secondary, Tertiary, and Additional Keyword Variations naturally where they fit.

Use keywords in:

- Headings where naturally appropriate
- Introductory content
- Relevant body sections
- FAQs where natural
- Other appropriate locations

Do not keyword stuff.

Do not force exact-match keywords into every heading.

Do not repeat the same keyword unnaturally.

Prioritize natural language, user intent, clarity, and usefulness.


==================================================
SEMANTIC RELEVANCE
==================================================

Use relevant semantic entities supplied in the research where they naturally help explain the subject.

Entities may include relevant:

- Products
- Services
- Brands
- Tools
- Processes
- Locations
- Industry concepts
- Businesses
- Other topic-specific entities

Use entities only when they are relevant to the surrounding content.

Do not force entities into the article simply to increase entity frequency.


==================================================
CONTENT GAPS
==================================================

Address the approved Step 3 content gaps wherever relevant.

Do not merely mention that a content gap exists.

Actually provide the useful information that fills the gap.

Do not create artificial statements such as:

"Unlike competitors..."

Instead, naturally provide the missing or under-covered information.


==================================================
VALUE ADDITIONS
==================================================

Incorporate the approved Step 3 value additions where they genuinely improve the page.

Use practical guidance, explanations, comparisons, examples, relevant data, processes, decision-making information, local insights, or other useful information where supported.

Do not add unsupported information simply because it was identified as a potential value addition.

Accuracy takes priority over completeness.


==================================================
CUSTOMER PAIN POINTS
==================================================

Use the supplied customer pain points to make the content genuinely useful.

Address relevant concerns naturally throughout the page.

Do not simply repeat the pain points as a list.

Explain the relevant problem, why it matters, and what information, product, service, or guidance is relevant to the customer's concern.

Do not promise that the business can solve a problem unless that capability is supported by the supplied business information.


==================================================
HUMAN-FIRST WRITING
==================================================

Write naturally and conversationally.

The content should feel written for real people rather than search engines.

Use:

- Clear language
- Natural transitions
- Appropriate contractions
- Varied sentence rhythm
- Direct explanations
- Empathy where appropriate
- Practical observations
- Natural questions
- Helpful context
- Appropriate uncertainty when facts vary

Avoid:

- Generic AI-style introductions
- Repetitive phrasing
- Empty motivational statements
- Excessive marketing language
- Unnecessary rhetorical questions
- Artificial conversational filler
- Repetitive conclusions
- Keyword stuffing
- Overly formal language where it does not suit the audience


==================================================
HUMAN VOICE
==================================================

Where appropriate and supported by the business information, use a conversational business voice.

Natural phrases such as:

"Here's the thing..."

"Now, you might be wondering..."

"It depends on..."

"Usually..."

"In many cases..."

may be used where they genuinely improve readability.

Do not insert conversational phrases mechanically.

Do not fabricate personal experiences.

Do not claim:

"In our experience..."

"Customers tell us..."

"We've seen..."

unless the supplied business information genuinely supports those statements.

Do not fabricate opinions, stories, customer experiences, or personal observations.


==================================================
READABILITY
==================================================

Write for easy comprehension.

Prefer:

- Short and clear sentences
- Active voice
- Direct explanations
- Short paragraphs
- Clear transitions
- Useful lists where appropriate
- Descriptive headings
- Scannable formatting

Avoid unnecessary complexity.

Vary sentence length naturally.

Do not sacrifice accuracy or meaning simply to make sentences shorter.


==================================================
LOCAL AND HYPER-LOCAL CONTENT
==================================================

For local pages, incorporate relevant local information supplied by the project.

Use local context where it genuinely helps the reader.

Only use supported:

- Locations
- Neighborhoods
- Areas
- Landmarks
- Local conditions
- Local market information
- Local customer considerations
- Seasonal information

Do not invent:

- Neighborhoods
- Landmarks
- Distances
- Statistics
- Local businesses
- Service areas
- Events
- Local claims


==================================================
CREDIBILITY AND TRUST
==================================================

Where the approved outline calls for credibility or trust, use only legitimate business information supplied in the project.

You may communicate supported:

- Experience
- Expertise
- Services
- Product range
- Business capabilities
- Certifications
- Licenses
- Awards
- Customer feedback
- Case studies
- Other legitimate trust signals

ONLY when supplied or supported.

Never invent:

- Years of experience
- Certifications
- Licenses
- Awards
- Customer numbers
- Reviews
- Testimonials
- Case studies
- Partnerships
- Guarantees
- Results


==================================================
TESTIMONIALS AND CASE STUDIES
==================================================

Do not fabricate testimonials.

Do not create fictional customer names.

Do not create fictional case studies.

Do not create composite testimonials and present them as real customer feedback.

If real testimonials or case studies are supplied, use them accurately.

If they are not supplied, do not fabricate them.

If the approved outline contains a trust section but no legitimate customer evidence is supplied, communicate trust through supported business information instead.


==================================================
PRODUCTS, SERVICES AND BUSINESS CAPABILITIES
==================================================

Only describe products, services, brands, equipment, processes, delivery methods, installation capabilities, or other business capabilities that are supported by the supplied business information or research.

Do not assume that a business offers something simply because competitors offer it.

Do not invent:

- Product availability
- Pricing
- Delivery times
- Installation services
- Guarantees
- Product brands
- Business capabilities


==================================================
PRICES, STATISTICS AND DATA
==================================================

Every numerical claim must be supported by the supplied research or business information.

If a number is not supported, do not invent it.

Where the supplied research supports a general range rather than an exact figure, communicate that appropriately.

Do not manufacture statistics to make the content appear authoritative.


==================================================
HEALTH, ECONOMIC AND TECHNICAL INFORMATION
==================================================

Where the approved outline calls for health, economic, technical, safety, or other factual information:

- Use only supplied or supported information.
- Do not exaggerate consequences.
- Do not make unsupported medical, financial, technical, or safety claims.
- Use appropriate qualification where outcomes vary.


==================================================
PROCESS / HOW IT WORKS
==================================================

If the approved outline includes a process or workflow section, explain the actual process supported by the supplied business information.

Do not invent:

- Steps
- Turnaround times
- Inspections
- Equipment
- Procedures
- Guarantees

Where a general process is discussed rather than a business-specific process, make that distinction clear.


==================================================
EQUIPMENT / TOOLS
==================================================

Only discuss equipment, tools, machinery, technology, or systems that are supported by the available information.

Explain why they matter when relevant.

Do not invent equipment simply because it would make the page sound more professional.


==================================================
PRACTICAL TIPS / DIY
==================================================

Where the approved outline includes practical tips or DIY guidance, provide useful information supported by the research.

Do not include unsafe or unsupported instructions.

Do not create DIY content simply because competitors have it if it is irrelevant to the page.


==================================================
FAQ / AEO CONTENT
==================================================

Use the approved Step 3 FAQ questions as the basis for the FAQ section.

Answer each question directly and clearly.

Keep answers concise enough to satisfy the question while providing the necessary context.

Use natural question wording.

Do not force FAQs into an unnatural format.

Do not invent answers when the available information does not support them.


==================================================
DIRECT ANSWERS
==================================================

Where appropriate, answer important customer questions clearly before expanding into additional explanation.

Structure relevant sections so that useful information can be understood without requiring the reader to search through long paragraphs.

Do not write unnaturally just to target a search feature.


==================================================
CONVERSION AND CTA
==================================================

Where the approved outline includes a CTA or conversion section, create a clear and natural next step aligned with the actual business.

Possible actions may include:

- Contacting the business
- Requesting information
- Shopping
- Booking
- Requesting a quote
- Another supported business action

Do not invent:

- Contact methods
- Offers
- Discounts
- Guarantees
- Pricing
- Availability


==================================================
SEO TITLE / META DESCRIPTION / H1
==================================================

Use the Step 3 SEO Title, Meta Description, and H1 as the approved strategic versions.

Do not unnecessarily change them.

The H1 must appear as the first Markdown H1 in the article:

# [Step 3 H1]

Do not create another H1 later in the article.


==================================================
CONTENT ORGANIZATION
==================================================

Follow the approved Step 3 outline from beginning to end.

For every approved outline section:

1. Use the specified heading level.
2. Use the approved heading concept.
3. Write the content required by the section's purpose and key points.
4. Incorporate relevant research and value additions.
5. Transition naturally to the next section.

Do not repeatedly restate the same information.

Do not create unnecessary sections.


==================================================
ANTI-HALLUCINATION PROTOCOL
==================================================

Accuracy takes priority over completeness.

Never invent information to fill a gap.

If information is unavailable:

- Omit the unsupported claim
- Use a general explanation where appropriate
- Clearly qualify uncertainty when supported
- Do not create fictional evidence

Never fabricate:

- Statistics
- Prices
- Dates
- Reviews
- Testimonials
- Case studies
- Customer stories
- Credentials
- Licenses
- Awards
- Business history
- Customer numbers
- Service areas
- Product availability
- Delivery times
- Guarantees
- Partnerships
- Results
- Local facts


==================================================
FINAL QUALITY CHECK
==================================================

Before returning the content, verify internally that:

- The approved Step 3 outline has been followed.
- The heading hierarchy is correct.
- The H1 is used once.
- H2 and H3 levels match the approved outline.
- The primary search intent is satisfied.
- Relevant customer pain points are addressed.
- Relevant content gaps are covered.
- Relevant value additions are included.
- Keywords are used naturally.
- Relevant semantic entities are used naturally.
- Local information is accurate and supported.
- Business claims are supported.
- No fictional testimonials or case studies were created.
- No unsupported statistics or claims were introduced.
- The content is useful rather than padded.
- The writing is clear and human-feeling.
- FAQs directly answer their questions.
- The CTA reflects actual business capabilities.
- No unsupported ranking guarantees are made.


==================================================
OUTPUT FORMAT
==================================================

Return ONLY the completed webpage content.

Do not explain your process.

Do not provide an analysis.

Do not provide a content strategy.

Do not provide a second outline.

Do not provide JSON.

Use clean Markdown formatting.

The output must contain the actual webpage content.

Use:

# for H1
## for H2
### for H3

The final content must follow the exact heading hierarchy established by Step 3.

Do not write:

H1:
H2:
H3:

as labels.

Instead, use actual Markdown headings.

The output should be ready to review, edit, and publish.


==================================================
PROJECT DATA
==================================================

PAGE INFORMATION

Page Name:
${selectedPage.pageName}

Page Type:
${selectedPage.pageType || 'Service Page'}

Page URL:
${selectedPage.targetUrl}

Targeted Location:
${project.targetedLocation || 'Not specified'}

Business Category / Industry:
${project.category || 'Not specified'}


KEYWORD ARCHITECTURE

Primary / Seed Keyword:
${selectedPage.primaryKeyword || 'Not specified'}

Primary Keyword Variations:
${selectedPage.primaryKeywordVariations && selectedPage.primaryKeywordVariations.length > 0 ? selectedPage.primaryKeywordVariations.join(', ') : 'None'}

Secondary Keyword:
${selectedPage.secondaryKeyword || 'Not specified'}

Secondary Keyword Variations:
${selectedPage.secondaryKeywordVariations && selectedPage.secondaryKeywordVariations.length > 0 ? selectedPage.secondaryKeywordVariations.join(', ') : 'None'}

Tertiary Keyword:
${selectedPage.tertiaryKeyword || 'Not specified'}

Tertiary Keyword Variations:
${selectedPage.tertiaryKeywordVariations && selectedPage.tertiaryKeywordVariations.length > 0 ? selectedPage.tertiaryKeywordVariations.join(', ') : 'None'}

Additional Keyword Variations:
None


COMPETITOR RESEARCH

Individual Competitor Headings:
${formattedCompetitorData}

Combined Competitor Headings Dataset:
${combinedHeadingsData}


CUSTOMER RESEARCH

Customer Pain Points & Research Insights:
${formattedPainPointsData}

FAQ / Question Research:
None


TOPICAL / LOCAL RESEARCH

Semantic Entities:
None

Local / Hyper-Local Information:
${project.targetedLocation || 'None'}

Additional Research / Data:
None


BUSINESS INFORMATION

Business Information:
${project.clientName} (${project.websiteUrl}) - ${project.category || 'Business'}


==================================================
STEP 3 APPROVED CONTENT STRATEGY
==================================================

SEO Title:
${outline?.seoTitle || 'Not set'}

Meta Description:
${outline?.metaDescription || 'Not set'}

H1:
${outline?.h1 || selectedPage.pageName}

Recommended Word Count:
${outline?.recommendedWordCount || 2500}

Approved Outline:
${(outline?.outlineSections || []).map((sec, i) => `${i + 1}. [${sec.headingTag}] ${sec.headingText}\n   Purpose: ${sec.purpose}\n   Key Points to Cover: ${sec.keyPoints.join('; ')}`).join('\n\n')}

Content Gaps:
${(outline?.contentGaps || []).map(g => `- ${g}`).join('\n') || 'None specified.'}

Value Additions:
${(outline?.valueAdditions || []).map(v => `- ${v}`).join('\n') || 'None specified.'}

Approved FAQs:
${(outline?.aeoFaqs || []).map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n') || 'None specified.'}
`;

  // Master AI Service Page Content Generation Prompt (for Service Pages)
  const servicePageGenerationPromptText = `Act as a senior SEO content strategist, local SEO strategist, EEAT-focused content planner, and conversion-focused service-page strategist.

Your task is to create a comprehensive, original, EEAT-driven content strategy and webpage outline for the selected commercial service page.

The goal is to create a professional, conversion-focused service webpage that satisfies search intent while providing genuinely useful information to potential customers.

The final outline must feel like the structure of a high-quality professional service business website — NOT like the table of contents of an SEO article or informational blog post.

==================================================
PROJECT INFORMATION
==================================================

Business Name:
${project.clientName}

Business Category / Industry:
${project.category || "Not specified"}

Page Name:
${selectedPage.pageName}

Page Type:
Service Page

Page URL:
${selectedPage.targetUrl}

Primary Service:
${selectedPage.pageName}

Target Location:
${project.targetedLocation || "Not specified"}

Primary Service Area:
${project.targetedLocation || "Not specified"}

Primary Keyword:
${selectedPage.primaryKeyword || "Not specified"}

Primary Keyword Variations:
${selectedPage.primaryKeywordVariations && selectedPage.primaryKeywordVariations.length > 0 ? selectedPage.primaryKeywordVariations.join(", ") : "None — RESEARCH IF RELEVANT"}

Secondary Keyword:
${selectedPage.secondaryKeyword || "Not specified"}

Secondary Keyword Variations:
${selectedPage.secondaryKeywordVariations && selectedPage.secondaryKeywordVariations.length > 0 ? selectedPage.secondaryKeywordVariations.join(", ") : "None — RESEARCH IF RELEVANT"}

Tertiary Keyword:
${selectedPage.tertiaryKeyword || "Not specified"}

Tertiary Keyword Variations:
${selectedPage.tertiaryKeywordVariations && selectedPage.tertiaryKeywordVariations.length > 0 ? selectedPage.tertiaryKeywordVariations.join(", ") : "None — RESEARCH IF RELEVANT"}

Additional Keyword Variations:
None — RESEARCH IF RELEVANT

Semantic Entities:
None — RESEARCH IF RELEVANT

==================================================
BUSINESS INFORMATION
==================================================

Services Included:
${selectedPage.pageName}

Service Limitations:
[INFORMATION REQUIRED]

Unique Selling Points:
None — RESEARCH/IDENTIFY OPPORTUNITIES IF RELEVANT, BUT DO NOT INVENT BUSINESS CLAIMS

Differentiators:
${project.category ? project.category + " specialist" : "None — RESEARCH/IDENTIFY OPPORTUNITIES IF RELEVANT, BUT DO NOT INVENT BUSINESS CLAIMS"}

Experience:
[INFORMATION REQUIRED — DO NOT INVENT]

Qualifications:
[INFORMATION REQUIRED — DO NOT INVENT]

Certifications:
[INFORMATION REQUIRED — DO NOT INVENT]

Licensing:
[INFORMATION REQUIRED — DO NOT INVENT]

Insurance:
[INFORMATION REQUIRED — DO NOT INVENT]

Guarantees / Warranties:
[INFORMATION REQUIRED — DO NOT INVENT]

Awards / Associations:
[INFORMATION REQUIRED — DO NOT INVENT]

Reviews / Testimonials:
[INFORMATION REQUIRED — DO NOT INVENT]

Case Studies:
[INFORMATION REQUIRED — DO NOT INVENT]

Pricing Information:
[INFORMATION REQUIRED — DO NOT INVENT]

Quote Process:
[INFORMATION REQUIRED — DO NOT INVENT]

Booking Method:
[INFORMATION REQUIRED — DO NOT INVENT]

Primary CTA:
Contact Us / Request a Quote

Secondary CTA:
Call Us

==================================================
CUSTOMER RESEARCH
==================================================

Primary Customer Problem:
${painPointsList[0]?.title || selectedPage.pageName + " service need"}

Customer Pain Points:
${formattedPainPointsData}

Customer Concerns:
${painPointsList.map(p => p.title).join("; ") || "None — RESEARCH IF RELEVANT"}

Customer Objections:
Cost, reliability, process transparency

Buying Motivations:
Service reliability, quality execution, clear communication

Desired Outcome:
High quality, long-lasting solution

Reasons Customers May Choose This Business:
Expert execution, local presence

Reasons Customers May Hesitate:
Unclear pricing, distrust

Common Customer Questions:
${(outline?.aeoFaqs || []).map(f => f.question).join("; ") || "None — RESEARCH IF RELEVANT"}

Customer Decision Factors:
Quality, experience, location, pricing transparency

==================================================
COMPETITOR RESEARCH
==================================================

Competitor #1:
${compHeadingsList[0]?.nameOrUrl || "Competitor #1"}

Competitor #1 Headings:
${compHeadingsList[0]?.headings?.join("\n  - ") || "None — RESEARCH IF RELEVANT"}

Competitor #2:
${compHeadingsList[1]?.nameOrUrl || "Competitor #2"}

Competitor #2 Headings:
${compHeadingsList[1]?.headings?.join("\n  - ") || "None — RESEARCH IF RELEVANT"}

Competitor #3:
${compHeadingsList[2]?.nameOrUrl || "Competitor #3"}

Competitor #3 Headings:
${compHeadingsList[2]?.headings?.join("\n  - ") || "None — RESEARCH IF RELEVANT"}

Competitor #4:
${compHeadingsList[3]?.nameOrUrl || "Competitor #4"}

Competitor #4 Headings:
${compHeadingsList[3]?.headings?.join("\n  - ") || "None — RESEARCH IF RELEVANT"}

Additional Competitor Research:
${combinedHeadingsData}

==================================================
LOCAL / TOPICAL RESEARCH
==================================================

Target Location:
${project.targetedLocation || "Not specified"}

Relevant Neighborhoods / Suburbs:
${project.targetedLocation || "None — RESEARCH IF RELEVANT"}

Relevant Local Conditions:
None — RESEARCH IF RELEVANT

Seasonal Considerations:
None — RESEARCH IF RELEVANT

Local Regulations / Building Characteristics:
None — RESEARCH IF RELEVANT

Relevant Local Entities:
${project.targetedLocation || "None — RESEARCH IF RELEVANT"}

Additional Research / Data:
None — RESEARCH IF RELEVANT

==================================================
FAQ / QUESTION RESEARCH
==================================================

FAQ / Question Research:
${(outline?.aeoFaqs || []).map(f => `Q: ${f.question}\nA: ${f.answer || ""}`).join("\n\n") || "None — RESEARCH IF RELEVANT"}

==================================================
APPROVED STEP 3 STRATEGY & OUTLINE
==================================================

SEO Title:
${outline?.seoTitle || 'Not set'}

Meta Description:
${outline?.metaDescription || 'Not set'}

H1:
${outline?.h1 || selectedPage.pageName}

Recommended Word Count:
${outline?.recommendedWordCount || 2500}

Approved Outline Sections:
${(outline?.outlineSections || []).map((sec, i) => `${i + 1}. [${sec.headingTag}] ${sec.headingText}\n   Purpose: ${sec.purpose}${sec.conversionRole ? `\n   Conversion Role: ${sec.conversionRole}` : ''}\n   Key Points to Cover: ${sec.keyPoints.join('; ')}`).join('\n\n')}

Content Gaps:
${(outline?.contentGaps || []).map(g => `- ${g}`).join('\n') || 'None specified.'}

Value Additions:
${(outline?.valueAdditions || []).map(v => `- ${v}`).join('\n') || 'None specified.'}

Approved FAQs:
${(outline?.aeoFaqs || []).map(f => `Q: ${f.question}\nA: ${f.answer}`).join('\n\n') || 'None specified.'}


==================================================
RESEARCH ENRICHMENT RULE
==================================================

The supplied project information is the primary source of truth.

However, a field marked:

- "None"
- "none"
- "N/A"
- "Not Available"
- "Not Supplied"
- "[RESEARCH REQUIRED]"
- "[INFORMATION REQUIRED]"

does NOT automatically mean that the information should be left empty.

If a research-capable field is marked "None", you are allowed and encouraged to conduct additional external research to improve the content strategy.

Use this priority:

1. Supplied business/project information
2. Supplied customer research
3. Supplied competitor research
4. Reliable external research
5. Strategic inference based on the above

Do not override verified business information with external assumptions.

==================================================
WHAT MAY BE RESEARCHED
==================================================

When relevant information is missing, external research may be used to enrich:

- Semantic entities
- Related concepts
- Industry terminology
- Keyword variations
- Search-intent language
- Customer questions
- FAQ opportunities
- Customer objections
- Buying considerations
- Competitor coverage
- Content gaps
- Value additions
- Local search context
- Relevant local topics
- Service considerations
- Industry concepts
- Related services
- Topical relationships

Research should improve the strategy rather than simply make the output longer.

==================================================
SEMANTIC ENTITY RESEARCH
==================================================

If Semantic Entities are supplied, use them as the starting point.

If Semantic Entities are "None", conduct your own research to identify relevant semantic entities.

Look at:

- The primary service
- Secondary services
- Target location
- Primary keyword
- Keyword variations
- Customer pain points
- Search intent
- Competitor coverage
- Industry terminology
- Relevant service concepts

Potential entities may include:

- Core services
- Service subtypes
- Property types
- Materials
- Processes
- Tools
- Equipment
- Industry concepts
- Customer problems
- Geographic entities
- Related services
- Relevant organizations
- Products
- Technologies
- Important service attributes

Only include entities that genuinely help establish topical relevance.

Do NOT create a large list of loosely related words.

Do NOT force semantic entities into the content simply to increase entity frequency.

==================================================
MISSING CUSTOMER RESEARCH
==================================================

If customer questions, objections, motivations, concerns, or decision factors are marked "None", conduct appropriate research to identify commercially relevant customer needs.

Prioritize questions and concerns that help a potential customer decide:

- Whether they need the service
- Whether the service is appropriate
- What the service includes
- What affects cost
- What risks or concerns exist
- What preparation may be required
- How to compare providers
- What information they need to request a quote
- What they should ask before purchasing

Do not turn every discovered question into an FAQ.

Group related questions where a cleaner structure is possible.

==================================================
MISSING COMPETITOR RESEARCH
==================================================

If competitor research is marked "None", research relevant competitors for the selected service and location where appropriate.

Use competitor research to identify:

- Common service topics
- Customer expectations
- Frequently addressed questions
- Commercial content patterns
- Trust signals
- Service-scope information
- Pricing topics
- Content gaps
- Potential differentiation opportunities

Competitor research is research evidence, NOT a template.

Never copy competitor wording, claims, headings, structure, testimonials, guarantees, or business facts.

==================================================
MISSING LOCAL RESEARCH
==================================================

If relevant local information is marked "None", conduct appropriate research to identify legitimate local context.

Potential research areas include:

- Target city
- Relevant neighborhoods
- Local property types
- Local environmental conditions
- Seasonal considerations
- Local regulations
- Local search terminology
- Relevant geographic entities

Only use information that can be reasonably verified.

Never invent local facts.

==================================================
MISSING KEYWORD RESEARCH
==================================================

If keyword variations are marked "None", conduct appropriate keyword research.

Identify:

- Natural keyword variations
- Search-intent variations
- Service-specific queries
- Location variations
- Commercial queries
- Long-tail questions

Do not create keyword lists simply for volume.

Only include terms that are genuinely relevant to the service and target audience.

==================================================
BUSINESS-SPECIFIC INFORMATION RULE
==================================================

External research must NEVER be used to invent or assume business-specific information.

Never infer that the business provides a capability merely because competitors provide it.

Never invent:

- Experience
- Years in business
- Certifications
- Licenses
- Insurance
- Awards
- Reviews
- Testimonials
- Case studies
- Customer numbers
- Guarantees
- Warranties
- Pricing
- Discounts
- Partnerships
- Specific equipment
- Specific products
- Specific processes
- Specific service areas
- Availability
- Turnaround times
- Results

If external research identifies something that could be useful but is not verified for the business, treat it as a general industry consideration or a recommended verification opportunity — never as a business claim.

==================================================
RESEARCH-DERIVED VS VERIFIED INFORMATION
==================================================

Always distinguish between:

A. VERIFIED BUSINESS INFORMATION
Information supplied by the project or reliably verified as belonging to the business.

B. GENERAL INDUSTRY INFORMATION
Information discovered through external research that generally applies to the service or industry.

C. STRATEGIC INFERENCE
Reasonable conclusions derived from the available research.

Never present B or C as A.

==================================================
CORE STRATEGIC OBJECTIVE
==================================================

Create a service-page content strategy that balances:

- Commercial search intent
- Transactional intent
- Local SEO relevance
- Customer needs
- Conversion opportunities
- EEAT
- Trust
- Service-scope clarity
- Pricing transparency
- Objection handling
- User experience
- Natural keyword usage
- Semantic relevance

Commercial intent should be the architectural backbone of the page.

The page should help visitors understand:

1. What service is offered
2. Who the service is for
3. Whether the service is relevant to their situation
4. What the service may include
5. What factors affect the service
6. What affects pricing
7. What concerns or objections they should consider
8. How to evaluate a provider
9. What they should clarify before purchasing
10. What questions they may have
11. What the next step is

Do not turn the page into a generic educational article.

==================================================
SERVICE-PAGE ARCHITECTURE
==================================================

The final structure must feel like a professional commercial service webpage.

Prioritize:

- Clear service positioning
- Service-specific information
- Customer decision-making
- Trust
- Scope clarity
- Pricing considerations
- Objection handling
- Conversion
- Local relevance

The outline should read like the architecture of a real professional service website.

It should NOT read like:

- A blog post
- An informational guide
- An academic article
- A generic SEO article
- A keyword map

Do not create sections merely because a topic exists.

Every section must earn its place.

==================================================
SECTION DISTINCTIVENESS RULE
==================================================

Every major section must have a materially different purpose.

Before creating each section, evaluate:

1. Does it address a different customer need?
2. Does it answer a different decision-making question?
3. Does it introduce materially different information?
4. Does it have a different conversion role?
5. Could it be merged with another section without losing meaningful information?

If two sections substantially overlap:

MERGE THEM

OR

Redefine one so that it serves a clearly different purpose.

NEVER create two consecutive sections that are simply different wording for the same topic.

For example, do NOT create:

"Window Cleaning for Scottsdale Homes and Businesses"

followed by:

"Residential and Commercial Window Cleaning Services"

if both sections primarily introduce residential and commercial window cleaning.

Instead, combine them or make their purposes clearly different.

==================================================
HEADING QUALITY
==================================================

Headings must be:

- Professional
- Crisp
- Specific
- Natural
- Concise
- Easy to scan
- Commercially appropriate
- Customer-focused

Headings should sound like headings on a high-quality professional service website.

Avoid headings that sound like AI-generated article titles.

Avoid unnecessary rhetorical language.

Avoid generic headings such as:

- Questions, Answered
- Everything You Need to Know
- What You Need to Know
- A Clearer Experience
- Making the Right Choice
- Why It Matters
- Your Complete Guide
- The Ultimate Guide
- Understanding [Topic]
- All About [Topic]

unless there is a strong user-facing reason to use them.

Prefer headings such as:

"Frequently Asked Questions About Window Cleaning in Scottsdale"

instead of:

"Window Cleaning Questions, Answered"

Prefer:

"What to Expect When Requesting Window Cleaning"

instead of:

"A Clearer Window Cleaning Experience From Quote to Completion"

Prefer:

"Before You Request a Window Cleaning Quote"

instead of:

"Questions to Consider Before Booking Window Cleaning"

Prefer:

"Choosing a Window Cleaning Company in Scottsdale"

instead of:

"How to Evaluate a Window Cleaning Provider in Scottsdale"

==================================================
HEADING NATURALNESS TEST
==================================================

Read every proposed heading as if it were displayed on the live website.

Ask:

"Would a credible professional service business actually use this heading?"

If not, rewrite it.

The heading should not feel:

- Academic
- Blog-like
- Keyword-stuffed
- Artificial
- Overly clever
- Overly conversational
- Excessively long

==================================================
HEADING PROGRESSION
==================================================

Create a logical commercial progression.

Where appropriate, the page may progress through:

1. Service positioning
2. Service options / property types / applications
3. Scope and inclusions
4. Specific customer problems
5. Important service considerations
6. Pricing considerations
7. Provider / trust evaluation
8. Customer preparation
9. FAQs
10. Conversion

This is a flexible framework, NOT a mandatory template.

Do not force every service page into the same structure.

Only use sections that genuinely fit the selected service.

==================================================
INTRODUCTION RULE
==================================================

The opening section must immediately establish:

- The service
- The business
- The location
- Who the service is relevant to
- The primary customer problem
- A natural next step

Do not create an introduction section and then immediately repeat the same service introduction in another section.

The opening section must provide meaningful commercial value.

==================================================
COMMERCIAL INTENT
==================================================

Commercial intent must remain visible throughout the architecture.

Each major section should contribute to the visitor's decision-making process.

Possible commercial purposes include:

- Understanding whether the service is needed
- Understanding service options
- Understanding what is included
- Understanding pricing factors
- Understanding limitations
- Comparing providers
- Evaluating trust
- Understanding preparation
- Resolving objections
- Deciding whether to request a quote
- Knowing what information to provide
- Understanding the next step

Do not make every section promotional.

Helpful information should support conversion naturally.

==================================================
CTA STRATEGY
==================================================

Plan CTAs strategically.

Do NOT automatically place a CTA after every section.

Too many repetitive CTAs can make a page feel overly sales-driven.

Instead, identify natural conversion points such as:

- After initial service positioning
- After service scope
- After pricing considerations
- After major objections
- Final conversion section

Use the supplied Primary CTA and Secondary CTA.

Do not invent:

- Phone numbers
- Email addresses
- Contact methods
- Offers
- Discounts
- Free estimates
- Availability
- Turnaround times
- Guarantees

unless supplied or verified.

==================================================
CUSTOMER PAIN POINTS
==================================================

Use customer research to shape the architecture.

For important pain points:

- Explain why the issue matters
- Address uncertainty
- Provide useful decision-making information
- Connect the issue to the relevant service consideration

Do not create a separate section for every pain point.

Group related pain points when doing so creates a cleaner professional structure.

==================================================
PRICING CONTENT
==================================================

Where pricing is relevant, address pricing uncertainty without inventing numbers.

Explain legitimate pricing factors supported by the research.

Potential factors may include:

- Quantity
- Size
- Complexity
- Access
- Property type
- Service scope
- Additional requested services
- Condition of the property

Do not invent:

- Price ranges
- Minimum charges
- Average prices
- Discounts
- Percentages
- Statistics

A pricing section should help reduce surprise-charge concerns and explain why a property-specific estimate may be more useful.

==================================================
TRUST AND EEAT
==================================================

Use only verified business information.

Where available, incorporate:

- Experience
- Qualifications
- Certifications
- Licensing
- Insurance
- Reviews
- Testimonials
- Case studies
- Awards
- Associations
- Guarantees
- Relevant business capabilities

Never invent these.

If important trust information is missing, identify it as a content gap or verification requirement.

Do not create unsupported claims such as:

- "Best in [location]"
- "[Location]'s #1"
- "Most trusted"
- "Award-winning"
- "Thousands of happy customers"
- "Industry-leading"

unless verified.

==================================================
COMPETITOR RESEARCH
==================================================

Use competitor research as background research only.

Analyze competitors for:

- Common topics
- Service expectations
- Customer questions
- Trust signals
- Commercial opportunities
- Content gaps
- Differentiation opportunities

Do NOT copy:

- Headings
- Wording
- Structure
- Testimonials
- Guarantees
- Claims
- Statistics
- Business facts

Do not mention competitors in the final strategy unless specifically required.

The goal is not to imitate competitors.

The goal is to create a cleaner, more useful, more focused service-page architecture.

==================================================
LOCAL SEO
==================================================

Use local information naturally where it improves the page.

Supported local information may include:

- Cities
- Neighborhoods
- Suburbs
- Local conditions
- Seasonal considerations
- Local property characteristics
- Regulations
- Geographic entities
- Local search behavior

Do not invent:

- Neighborhoods
- Service areas
- Distances
- Landmarks
- Statistics
- Local businesses
- Events
- Local claims

Do not repeat the location unnecessarily.

==================================================
KEYWORD STRATEGY
==================================================

Use keywords naturally and strategically.

Primary keywords may appear in:

- SEO title
- H1
- Introduction
- Relevant H2s
- Body content
- FAQs
- Meta description

Secondary and tertiary keywords should only be used where natural.

Do not force exact-match keywords into every heading.

Do not create a heading solely because a keyword variation exists.

Do not repeat keywords unnaturally.

Topic relevance, search intent, clarity, and natural language take priority.

==================================================
FAQ / AEO STRATEGY
==================================================

Create a focused FAQ section based on:

- Supplied customer questions
- Researched customer questions
- Customer objections
- Search intent
- Competitor coverage
- Important pre-purchase uncertainties

Avoid generic questions that do not help a potential customer make a decision.

Use a professional, specific FAQ heading.

Prefer:

"Frequently Asked Questions About [SERVICE] in [LOCATION]"

over:

"Questions, Answered"

FAQ questions should cover meaningful decisions such as:

- Pricing
- Service scope
- Availability
- Suitability
- Difficult situations
- Preparation
- Frequency
- Provider selection
- Quote requirements

Only include questions relevant to the selected service.

==================================================
CONTENT GAPS
==================================================

Identify meaningful content gaps such as:

- Missing service-scope information
- Missing pricing clarity
- Missing trust information
- Missing customer decision guidance
- Missing local relevance
- Missing preparation information
- Missing objection handling
- Missing FAQ coverage
- Missing service limitations
- Missing business-specific verification

If supplied content gaps are "None", research and identify genuine gaps.

Do not create artificial gaps.

==================================================
VALUE ADDITIONS
==================================================

Identify useful additions that would improve the page beyond generic competitor coverage.

Potential value additions include:

- Customer-first service-scope explanation
- Pricing-factor framework
- Pre-booking checklist
- Customer decision checklist
- Provider-evaluation guidance
- Objection handling
- Service comparison
- Local considerations
- Quote-request guidance
- Practical examples where supported

If supplied value additions are "None", research and identify appropriate opportunities.

Do not add filler.

==================================================
CONTENT DEPTH
==================================================

Recommend an appropriate total word count based on:

- Search intent
- Topic complexity
- Competitor depth
- Customer needs
- Number of meaningful sections
- Amount of useful information available

Do not recommend excessive length simply because competitors have long pages.

Do not create filler sections to reach a word count.

==================================================
SEO TITLE / META DESCRIPTION / H1
==================================================

Create:

- SEO title
- Meta description
- H1

The primary keyword and location should be incorporated naturally where appropriate.

The H1 should clearly communicate the service and target location.

Avoid keyword stuffing.

==================================================
APPROVED STRATEGY HANDLING
==================================================

If an approved Step 3 outline already exists in the supplied information, treat it as the source of truth.

Do not redesign it unless explicitly instructed.

If no approved outline exists, create the strongest structure supported by the research.

==================================================
FINAL ANTI-REDUNDANCY REVIEW
==================================================

Before returning the outline, review every section together.

Check for repeated coverage of:

- Service introduction
- Residential/commercial service types
- Service scope
- Pricing
- Trust
- Customer concerns
- FAQs
- Local relevance
- CTA

Each major topic should have a clear primary home within the architecture.

Information can be referenced elsewhere when useful, but should not be repeatedly explained in full.

If two sections can be merged without losing meaningful value, merge them.

==================================================
FINAL PROFESSIONAL WEBSITE TEST
==================================================

Imagine all headings displayed together on the live webpage.

The structure should feel:

- Professional
- Crisp
- Clean
- Modern
- Commercial
- Trustworthy
- Easy to scan
- Customer-focused
- Locally relevant
- Conversion-oriented

It should NOT feel:

- Like an SEO article
- Like an academic paper
- Like an AI-generated blog
- Like a keyword list
- Like a collection of competitor headings
- Repetitive
- Overly verbose
- Overly salesy

==================================================
OUTPUT FORMAT
==================================================

Return exactly ONE valid JSON object.

Do not use Markdown.

Do not use code fences.

Do not provide explanations.

Do not provide analysis.

Do not provide commentary before or after the JSON.

Use double quotes for all JSON keys and string values.

Do not use trailing commas.

Use exactly this structure:

{
  "seoTitle": "...",
  "metaDescription": "...",
  "h1": "...",
  "recommendedWordCount": 0,
  "searchIntent": {
    "primaryIntent": "...",
    "secondaryIntents": [
      "...",
      "..."
    ]
  },
  "outlineSections": [
    {
      "headingTag": "H2",
      "headingText": "...",
      "purpose": "...",
      "conversionRole": "...",
      "keyPoints": [
        "...",
        "..."
      ]
    }
  ],
  "aeoFaqs": [
    {
      "question": "...",
      "answer": "..."
    }
  ],
  "contentGaps": [
    "...",
    "..."
  ],
  "valueAdditions": [
    "...",
    "..."
  ]
}

==================================================
FINAL VALIDATION
==================================================

Before returning the JSON, verify internally that:

- The page is clearly a commercial service page.
- The primary search intent is satisfied.
- The architecture is conversion-focused.
- The opening section does not duplicate the following section.
- No two adjacent sections substantially overlap.
- Every major section has a distinct purpose.
- Headings are concise, professional, and crisp.
- Headings sound appropriate for a real professional service website.
- Generic AI/article-style headings have been avoided.
- "Questions, Answered" has not been used where a more specific heading is appropriate.
- FAQ heading is specific and professional, such as "Frequently Asked Questions About [Service] in [Location]".
- The outline does not read like a blog article.
- The structure is not unnecessarily long.
- Customer pain points are addressed.
- Customer objections are addressed where relevant.
- Content gaps are addressed.
- Value additions are incorporated.
- Competitor research has informed coverage without copying competitors.
- Semantic entities are included where useful.
- If semantic entities were supplied as "None", relevant entities have been researched where appropriate.
- If other research-capable fields were supplied as "None", they have been researched where useful.
- Keywords are used naturally.
- Local information is accurate and supported.
- Business claims are verified or clearly identified as requiring verification.
- No unsupported pricing has been invented.
- No unsupported credentials have been invented.
- No fictional testimonials or case studies have been created.
- No unsupported statistics have been introduced.
- CTAs are strategically placed rather than mechanically repeated.
- The recommended word count is reasonable.
- The final architecture is clean, logical, professional, and ready for Step 4 content generation.`;

  const generationPromptText = isHomepage ? defaultGenerationPromptText : servicePageGenerationPromptText;

  // Synthetic Content Generation Engine (Requirement 5 & 18)
  const handleGenerateContent = () => {
    if (!outline) {
      setErrorState('Content strategy outline is required before generating webpage content.');
      return;
    }

    setIsGenerating(true);
    setErrorState(null);
    setGenerationStep(1);

    // Stage 1: Baseline & Keyword verification
    setTimeout(() => {
      setGenerationStep(2);
      // Stage 2: Competitor research & gap integration
      setTimeout(() => {
        setGenerationStep(3);
        // Stage 3: Strategy outline parsing
        setTimeout(() => {
          setGenerationStep(4);
          // Stage 4: Writing article content
          setTimeout(() => {
            try {
              const generated = synthesizeFullArticle(selectedPage, project, outline);
              updateCurrentPage({
                generatedContent: generated,
                status: 'completed',
              });
              setEditedContentText(generated);
              setIsGenerating(false);
              setGenerationStep(0);
            } catch {
              setIsGenerating(false);
              setGenerationStep(0);
              setErrorState('Unable to generate the content. Your strategy and research have been preserved. Try again.');
            }
          }, 600);
        }, 500);
      }, 500);
    }, 400);
  };

  // Article Generation Helper
  const synthesizeFullArticle = (
    page: ContentPlanningPage,
    proj: ContentPlanningProject,
    out: PageFinalOutline
  ): string => {
    const lines: string[] = [];

    // Title / H1
    lines.push(`# ${out.h1 || page.pageName}\n`);

    // Introduction
    const locationStr = proj.targetedLocation ? ` in ${proj.targetedLocation}` : '';
    lines.push(`Finding reliable, top-quality solutions${locationStr} requires clear information, expert standards, and proven results. Whether you are looking for specialized services, key product guidance, or comprehensive support for **${page.primaryKeyword || page.pageName}**, making informed decisions is critical.\n`);
    lines.push(`At **${proj.clientName}**, we combine deep industry experience with customer-focused solutions to deliver dependable quality. Below, we break down everything you need to know about ${page.primaryKeyword || page.pageName}, addressing key service considerations, process workflows, and critical answers to common questions.\n`);

    // Outline Sections
    (out.outlineSections || []).forEach((sec) => {
      const tag = sec.headingTag === 'H3' ? '###' : '##';
      lines.push(`${tag} ${sec.headingText}\n`);
      if (sec.purpose) {
        lines.push(`${sec.purpose}\n`);
      }

      if (sec.keyPoints && sec.keyPoints.length > 0) {
        sec.keyPoints.forEach(point => {
          lines.push(`- **${point}**: Clear, comprehensive guidance designed to provide immediate value, address operational needs, and support informed customer choices.`);
        });
        lines.push('');
      } else {
        lines.push(`Our team ensures every aspect of this service is handled with absolute precision, utilizing commercial-grade standards, experienced personnel, and verified quality protocols.\n`);
      }
    });

    // Content Gaps & Value Additions Integration
    if (out.contentGaps && out.contentGaps.length > 0) {
      lines.push(`## Key Considerations & Expert Insights\n`);
      lines.push(`Unlike standard service options that overlook critical technical details, our approach directly addresses essential factors that impact long-term performance:\n`);
      out.contentGaps.forEach(gap => {
        lines.push(`- **${gap}**: Addressed systematically through expert oversight and tailored execution.`);
      });
      lines.push('');
    }

    if (out.valueAdditions && out.valueAdditions.length > 0) {
      lines.push(`### Value-Added Standards\n`);
      out.valueAdditions.forEach(val => {
        lines.push(`- **${val}**: Implemented to give our customers superior transparency and peace of mind.`);
      });
      lines.push('');
    }

    // FAQs / AEO Questions
    if (out.aeoFaqs && out.aeoFaqs.length > 0) {
      lines.push(`## Frequently Asked Questions\n`);
      out.aeoFaqs.forEach(faq => {
        lines.push(`### ${faq.question}`);
        lines.push(`${faq.answer || 'Contact our technical team for personalized advice tailored to your specific requirements.'}\n`);
      });
    }

    // Call to Action / Business Conclusion
    lines.push(`## Get Started with ${proj.clientName}\n`);
    lines.push(`Ready to experience the difference that true quality and professional expertise make? Contact **${proj.clientName}** today to discuss your project requirements or request a comprehensive quote${locationStr}.\n`);
    lines.push(`- **Website**: [${proj.websiteUrl}](https://${proj.websiteUrl})`);
    if (proj.targetedLocation) {
      lines.push(`- **Service Area**: ${proj.targetedLocation}`);
    }

    return lines.join('\n');
  };

  // Copy Content handler (copies clean text, not JSON - Req 12)
  const handleCopyContent = () => {
    const textToCopy = selectedPage.generatedContent || editedContentText;
    if (!textToCopy) return;
    navigator.clipboard.writeText(textToCopy);
    setCopiedContent(true);
    setTimeout(() => setCopiedContent(false), 2500);
  };

  // Save manual inline edits (Req 13)
  const handleSaveEdits = () => {
    updateCurrentPage({
      generatedContent: editedContentText,
    });
    setIsEditing(false);
  };

  // Handle pasting article directly from ChatGPT/Gemini
  const handleImportPastedArticle = () => {
    if (!pastedArticleText.trim()) return;
    updateCurrentPage({
      generatedContent: pastedArticleText.trim(),
      status: 'completed',
    });
    setEditedContentText(pastedArticleText.trim());
    setPastedArticleText('');
    setShowPromptBox(false);
  };

  // Export handlers
  const handleDownloadMarkdown = () => {
    const text = selectedPage.generatedContent || editedContentText;
    if (!text) return;
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedPage.pageName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-content.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Word count calculator for generated content
  const currentContentText = selectedPage.generatedContent || editedContentText || '';
  const actualWordCount = currentContentText.trim() ? currentContentText.trim().split(/\s+/).length : 0;

  // Render EMPTY STATE if Step 3 outline is missing (Requirement 16)
  if (!outline) {
    return (
      <div style={{ paddingBottom: '60px' }}>
        {/* Page Selector */}
        {pages.length > 1 && (
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#94a3b8' }}>Select Page:</span>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {pages.map(p => (
                <button
                  key={p.id}
                  onClick={() => setSelectedPageId(p.id)}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '8px',
                    fontSize: '12.5px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: p.id === selectedPage.id ? '1px solid #0284c7' : '1px solid #1e293b',
                    background: p.id === selectedPage.id ? '#0284c7' : '#070c16',
                    color: '#ffffff',
                  }}
                >
                  {p.pageName}
                </button>
              ))}
            </div>
          </div>
        )}

        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '56px 32px', textAlign: 'center', maxWidth: '720px', margin: '30px auto' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '16px', background: 'rgba(245, 158, 11, 0.1)', border: '1px solid rgba(245, 158, 11, 0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px auto' }}>
            <AlertCircle size={32} color="#f59e0b" />
          </div>

          <h3 style={{ fontSize: '20px', color: '#ffffff', fontWeight: 800, margin: '0 0 10px 0' }}>
            Content Strategy Required
          </h3>

          <p style={{ fontSize: '14.5px', color: '#94a3b8', margin: '0 0 24px 0', lineHeight: 1.6 }}>
            Generate your content strategy and outline in <strong>Step 3</strong> before creating the webpage content for <strong>{selectedPage.pageName}</strong>.
          </p>

          <button
            onClick={() => {
              if (onGoToStep3) onGoToStep3();
            }}
            style={{
              background: 'linear-gradient(135deg, #0284c7 0%, #0369a1 100%)',
              color: '#ffffff',
              border: 'none',
              padding: '12px 28px',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              boxShadow: '0 4px 14px rgba(2, 132, 199, 0.4)',
            }}
          >
            <span>Go to Step 3: Content Strategy & Outline</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '60px' }}>
      {/* 1. STEP 4 SCREEN HEADER (Requirement 1 & 21) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <span style={{ background: '#0284c7', color: '#ffffff', fontSize: '11px', fontWeight: 900, padding: '3px 8px', borderRadius: '6px', letterSpacing: '0.5px' }}>
              STEP 4 OF 4
            </span>
            <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#ffffff', margin: 0, letterSpacing: '-0.3px' }}>
              Content Generation
            </h2>
          </div>
          <p style={{ fontSize: '13.5px', color: '#94a3b8', margin: 0, maxWidth: '800px', lineHeight: 1.5 }}>
            Turn your approved content strategy into the final webpage content using the research, outline, and business information collected throughout the workflow.
          </p>
        </div>

        {/* Page Selector Tabs */}
        {pages.length > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b' }}>Page:</span>
            {pages.map(p => (
              <button
                key={p.id}
                onClick={() => setSelectedPageId(p.id)}
                style={{
                  padding: '7px 14px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: p.id === selectedPage.id ? '1px solid #0284c7' : '1px solid #1e293b',
                  background: p.id === selectedPage.id ? '#0284c7' : '#070c16',
                  color: '#ffffff',
                  transition: 'all 0.15s ease',
                }}
              >
                {p.pageName}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* 2. TOP PAGE CONTEXT CARD (Requirement 2) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#0284c7', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '8px' }}>
          PAGE CONTEXT & SEED KEYWORD
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                {selectedPage.pageName}
              </h3>
              <span style={{ background: '#0f172a', border: '1px solid #334155', padding: '3px 10px', borderRadius: '6px', fontSize: '11.5px', color: '#38bdf8', fontWeight: 700 }}>
                {selectedPage.pageType || 'Service Page'}
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', fontSize: '12.5px', color: '#94a3b8' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                <Globe size={13} color="#64748b" />
                <a href={selectedPage.targetUrl} target="_blank" rel="noreferrer" style={{ color: '#38bdf8', textDecoration: 'none' }}>
                  {selectedPage.targetUrl}
                </a>
              </span>

              {project.targetedLocation && (
                <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <MapPin size={13} color="#64748b" />
                  Targeting: <strong style={{ color: '#ffffff' }}>{project.targetedLocation}</strong>
                </span>
              )}
            </div>
          </div>

          <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '10px 16px', borderRadius: '10px', textAlign: 'right' }}>
            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '2px' }}>
              Primary / Seed Keyword
            </div>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Target size={14} color="#f59e0b" />
              <span>{selectedPage.primaryKeyword || 'Not specified'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* 3. STRATEGY SUMMARY CARD (Requirement 3) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', padding: '20px 24px', marginBottom: '20px' }}>
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '12px' }}>
          CONTENT STRATEGY SUMMARY
        </div>

        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '12px', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px' }}>
            Recommended H1 Heading
          </div>
          <div style={{ fontSize: '16px', fontWeight: 800, color: '#ffffff', background: '#090e1a', border: '1px solid #1e293b', padding: '10px 16px', borderRadius: '8px' }}>
            {outline.h1}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', paddingTop: '12px', borderTop: '1px solid #1e293b' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <span style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981', padding: '5px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 800 }}>
              Target Length: ~{outline.recommendedWordCount || 2500} words
            </span>

            {selectedPage.generatedContent && (
              <span style={{ background: 'rgba(56, 189, 248, 0.1)', border: '1px solid rgba(56, 189, 248, 0.3)', color: '#38bdf8', padding: '5px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 800 }}>
                Actual Generated: {actualWordCount} words
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', fontSize: '12px' }}>
            <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '5px 10px', borderRadius: '6px', color: '#cbd5e1', fontWeight: 700 }}>
              {(outline.outlineSections || []).length} Sections
            </span>
            <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '5px 10px', borderRadius: '6px', color: '#f59e0b', fontWeight: 700 }}>
              {(outline.contentGaps || []).length} Content Gaps
            </span>
            <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '5px 10px', borderRadius: '6px', color: '#38bdf8', fontWeight: 700 }}>
              {(outline.valueAdditions || []).length} Value Additions
            </span>
            <span style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '5px 10px', borderRadius: '6px', color: '#a855f7', fontWeight: 700 }}>
              {(outline.aeoFaqs || []).length} FAQs
            </span>
          </div>
        </div>
      </div>

      {/* 4. APPROVED CONTENT OUTLINE (COLLAPSIBLE) (Requirement 4) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', marginBottom: '20px', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setIsOutlineExpanded(!isOutlineExpanded)}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: '#070c16',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileText size={16} color="#38bdf8" />
            <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
              Approved Content Outline ({outline.outlineSections?.length || 0} Sections)
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
            <span>{isOutlineExpanded ? 'Hide Outline' : 'View Outline'}</span>
            {isOutlineExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>

        {isOutlineExpanded && (
          <div style={{ padding: '0 24px 20px 24px', borderTop: '1px solid #1e293b', paddingTop: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {(outline.outlineSections || []).map((sec, idx) => (
                <div key={sec.id || idx} style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 900, color: '#64748b' }}>
                      #{String(idx + 1).padStart(2, '0')}
                    </span>
                    <span style={{ background: sec.headingTag === 'H3' ? '#1e1b4b' : '#0369a1', color: '#ffffff', fontSize: '10.5px', fontWeight: 800, padding: '2px 7px', borderRadius: '4px' }}>
                      {sec.headingTag || 'H2'}
                    </span>
                    <span style={{ fontSize: '13.5px', fontWeight: 700, color: '#ffffff' }}>
                      {sec.headingText}
                    </span>
                  </div>

                  {sec.purpose && (
                    <p style={{ fontSize: '12px', color: '#94a3b8', margin: '0 0 6px 0', lineHeight: 1.4 }}>
                      {sec.purpose}
                    </p>
                  )}

                  {sec.keyPoints && sec.keyPoints.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '6px' }}>
                      {sec.keyPoints.map((kp, kpIdx) => (
                        <span key={kpIdx} style={{ background: '#070c16', border: '1px solid #1e293b', color: '#cbd5e1', fontSize: '11px', padding: '3px 8px', borderRadius: '4px' }}>
                          • {kp}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 5. GENERATION ACTION & LOADING STATE (Requirement 5 & 6) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '24px', marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h4 style={{ fontSize: '15px', fontWeight: 800, color: '#ffffff', margin: '0 0 4px 0' }}>
              {selectedPage.generatedContent ? 'Regenerate Webpage Content' : 'Generate Full Webpage Content'}
            </h4>
            <p style={{ fontSize: '12.5px', color: '#94a3b8', margin: 0 }}>
              Generates long-form, highly structured publication-ready webpage content directly from your Step 3 strategy.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => setShowPromptBox(!showPromptBox)}
              style={{
                background: '#090e1a',
                color: '#38bdf8',
                border: '1px solid #1e293b',
                padding: '11px 18px',
                borderRadius: '10px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <FileCode size={16} />
              <span>{showPromptBox ? 'Hide Prompt & Paste' : 'Prompt & Paste Tool'}</span>
            </button>

            <button
              type="button"
              onClick={handleGenerateContent}
              disabled={isGenerating}
              style={{
                background: isGenerating
                  ? '#1e293b'
                  : selectedPage.generatedContent
                  ? 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)'
                  : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: '#ffffff',
                border: 'none',
                padding: '11px 24px',
                borderRadius: '10px',
                fontSize: '13.5px',
                fontWeight: 800,
                cursor: isGenerating ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: isGenerating ? 'none' : '0 4px 14px rgba(16, 185, 129, 0.4)',
                transition: 'all 0.15s ease',
              }}
            >
              {isGenerating ? <RefreshCw size={16} className="animate-spin" /> : <Sparkles size={16} />}
              <span>
                {isGenerating
                  ? 'Generating webpage...'
                  : selectedPage.generatedContent
                  ? 'Regenerate Content'
                  : 'Generate Content'}
              </span>
            </button>
          </div>
        </div>

        {/* PROMPT & PASTE TOOL COLLAPSIBLE */}
        {showPromptBox && (
          <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #1e293b' }}>
            {/* Prompt Tab Switcher Buttons (Setup First, Generation Second) */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => setPromptTab('setup')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: promptTab === 'setup' ? '1px solid #10b981' : '1px solid #1e293b',
                  background: promptTab === 'setup' ? 'linear-gradient(135deg, #059669 0%, #10b981 100%)' : '#090e1a',
                  color: '#ffffff',
                  transition: 'all 0.15s ease',
                  boxShadow: promptTab === 'setup' ? '0 2px 8px rgba(16, 185, 129, 0.3)' : 'none',
                }}
              >
                1. Service Page Content Setup Brief (Use First)
              </button>
              <button
                type="button"
                onClick={() => setPromptTab('generation')}
                style={{
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: promptTab === 'generation' ? '1px solid #0284c7' : '1px solid #1e293b',
                  background: promptTab === 'generation' ? 'linear-gradient(135deg, #0369a1 0%, #0284c7 100%)' : '#090e1a',
                  color: '#ffffff',
                  transition: 'all 0.15s ease',
                  boxShadow: promptTab === 'generation' ? '0 2px 8px rgba(2, 132, 199, 0.3)' : 'none',
                }}
              >
                2. Webpage Content Generation Prompt (Use Second)
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
              {/* Left Column: Active Selected Prompt */}
              <div>
                {promptTab === 'setup' ? (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#10b981', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Step 4A: Service Page Content Setup & Inputs Brief (Use First)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(servicePageContentSetupPromptText);
                          setCopiedSetupPrompt(true);
                          setTimeout(() => setCopiedSetupPrompt(false), 2500);
                        }}
                        style={{
                          background: '#090e1a',
                          border: '1px solid #1e293b',
                          color: '#10b981',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {copiedSetupPrompt ? <Check size={12} /> : <Copy size={12} />}
                        <span>{copiedSetupPrompt ? 'Copied Brief!' : 'Copy Setup Brief'}</span>
                      </button>
                    </div>

                    <p style={{ fontSize: '11.5px', color: '#94a3b8', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                      <strong>Purpose of Prompt #1:</strong> Use this preparation prompt first to organize service page data, gather business facts, verify customer pain points, and complete content readiness checks before writing.
                    </p>

                    <textarea
                      readOnly
                      rows={8}
                      value={servicePageContentSetupPromptText}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        background: '#090e1a',
                        border: '1px solid #1e293b',
                        color: '#cbd5e1',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        resize: 'vertical',
                      }}
                    />
                  </>
                ) : (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                      <span style={{ fontSize: '12px', fontWeight: 800, color: '#38bdf8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        Step 4B: Webpage Content Generation Master Prompt (Use Second)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(generationPromptText);
                          setCopiedPrompt(true);
                          setTimeout(() => setCopiedPrompt(false), 2500);
                        }}
                        style={{
                          background: '#090e1a',
                          border: '1px solid #1e293b',
                          color: '#38bdf8',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 700,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        {copiedPrompt ? <Check size={12} /> : <Copy size={12} />}
                        <span>{copiedPrompt ? 'Copied Prompt!' : 'Copy Generation Prompt'}</span>
                      </button>
                    </div>

                    <p style={{ fontSize: '11.5px', color: '#94a3b8', margin: '0 0 10px 0', lineHeight: 1.4 }}>
                      <strong>Purpose of Prompt #2:</strong> Use this drafting prompt second to turn your approved Step 3 strategy outline and Step 4A inputs into the complete, publish-ready webpage content.
                    </p>

                    <textarea
                      readOnly
                      rows={8}
                      value={generationPromptText}
                      style={{
                        width: '100%',
                        padding: '10px',
                        borderRadius: '8px',
                        background: '#090e1a',
                        border: '1px solid #1e293b',
                        color: '#cbd5e1',
                        fontSize: '11px',
                        fontFamily: 'monospace',
                        resize: 'vertical',
                      }}
                    />
                  </>
                )}
              </div>

              {/* Right Column: Paste AI Article Response */}
              <div>
                <span style={{ fontSize: '12px', fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', display: 'block', marginBottom: '8px' }}>
                  Paste AI Article Response
                </span>

                <textarea
                  rows={5}
                  placeholder="Paste long-form Markdown article response from ChatGPT / Gemini / Claude here..."
                  value={pastedArticleText}
                  onChange={e => setPastedArticleText(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '8px',
                    background: '#090e1a',
                    border: '1px solid #1e293b',
                    color: '#ffffff',
                    fontSize: '11.5px',
                    fontFamily: 'monospace',
                    marginBottom: '10px',
                    resize: 'vertical',
                  }}
                />

                <button
                  type="button"
                  onClick={handleImportPastedArticle}
                  disabled={!pastedArticleText.trim()}
                  style={{
                    background: pastedArticleText.trim() ? '#f59e0b' : '#1e293b',
                    color: pastedArticleText.trim() ? '#070c16' : '#64748b',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 800,
                    cursor: pastedArticleText.trim() ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <Check size={14} />
                  <span>Import & Save Article</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 6. GENERATION STATE CHECKLIST (Requirement 6) */}
        {isGenerating && (
          <div style={{ marginTop: '20px', background: '#090e1a', border: '1px solid #0284c7', borderRadius: '12px', padding: '20px' }}>
            <div style={{ fontSize: '14px', fontWeight: 800, color: '#38bdf8', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw size={16} className="animate-spin" />
              <span>Generating your webpage...</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '12.5px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: generationStep >= 1 ? '#10b981' : '#64748b' }}>
                <CheckCircle2 size={16} color={generationStep >= 1 ? '#10b981' : '#334155'} />
                <span>Keyword & Page Baseline</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: generationStep >= 2 ? '#10b981' : '#64748b' }}>
                <CheckCircle2 size={16} color={generationStep >= 2 ? '#10b981' : '#334155'} />
                <span>Competitor Research</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: generationStep >= 3 ? '#10b981' : '#64748b' }}>
                <CheckCircle2 size={16} color={generationStep >= 3 ? '#10b981' : '#334155'} />
                <span>Content Strategy & Outline</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: generationStep >= 4 ? '#38bdf8' : '#64748b', fontWeight: generationStep >= 4 ? 700 : 400 }}>
                <RefreshCw size={14} className={generationStep >= 4 ? 'animate-spin' : ''} color={generationStep >= 4 ? '#38bdf8' : '#334155'} />
                <span>Content Generation</span>
              </div>
            </div>
          </div>
        )}

        {/* 15. ERROR STATE (Requirement 15) */}
        {errorState && (
          <div style={{ marginTop: '16px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: '10px', padding: '14px 18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#f87171', fontSize: '13px', fontWeight: 600 }}>
              <AlertCircle size={16} />
              <span>{errorState}</span>
            </div>
            <button
              onClick={handleGenerateContent}
              style={{ background: '#ef4444', color: '#ffffff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: 700, cursor: 'pointer' }}
            >
              Retry
            </button>
          </div>
        )}
      </div>

      {/* 7. GENERATED CONTENT AREA & PREVIEW (Requirement 7 & 8) */}
      {selectedPage.generatedContent ? (
        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '32px', marginBottom: '24px' }}>
          {/* Header Action Bar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '14px', paddingBottom: '20px', borderBottom: '1px solid #1e293b', marginBottom: '28px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Sparkles size={20} color="#10b981" />
              <h3 style={{ fontSize: '18px', fontWeight: 800, color: '#ffffff', margin: 0 }}>
                Generated Content
              </h3>
              <span style={{ background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#10b981', padding: '3px 10px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 800 }}>
                {actualWordCount} Words
              </span>
            </div>

            {/* Content Actions (Requirement 12) */}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleCopyContent}
                style={{
                  background: copiedContent ? '#10b981' : '#090e1a',
                  color: copiedContent ? '#ffffff' : '#cbd5e1',
                  border: '1px solid #1e293b',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                {copiedContent ? <Check size={14} /> : <Copy size={14} />}
                <span>{copiedContent ? 'Copied Content!' : 'Copy Content'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  if (isEditing) {
                    handleSaveEdits();
                  } else {
                    setEditedContentText(selectedPage.generatedContent || '');
                    setIsEditing(true);
                  }
                }}
                style={{
                  background: isEditing ? '#0284c7' : '#090e1a',
                  color: '#ffffff',
                  border: '1px solid #1e293b',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Edit3 size={14} />
                <span>{isEditing ? 'Save Changes' : 'Edit Content'}</span>
              </button>

              <button
                type="button"
                onClick={handleDownloadMarkdown}
                style={{
                  background: '#090e1a',
                  color: '#94a3b8',
                  border: '1px solid #1e293b',
                  padding: '8px 14px',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <Download size={14} />
                <span>Export Markdown</span>
              </button>

              <button
                type="button"
                onClick={() => ContentPlanningPdfService.exportProjectPdfReport(project)}
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: '#ffffff',
                  border: 'none',
                  padding: '8px 16px',
                  borderRadius: '8px',
                  fontSize: '12.5px',
                  fontWeight: 800,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
                }}
              >
                <FileText size={14} />
                <span>Export Final Report (PDF)</span>
              </button>
            </div>
          </div>

          {/* EDITING EXPERIENCE (Requirement 13) */}
          {isEditing ? (
            <div>
              <div style={{ fontSize: '12px', fontWeight: 700, color: '#0284c7', marginBottom: '8px' }}>
                Editing Webpage Content (Markdown Format):
              </div>
              <textarea
                rows={22}
                value={editedContentText}
                onChange={e => setEditedContentText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '12px',
                  background: '#090e1a',
                  border: '1px solid #0284c7',
                  color: '#ffffff',
                  fontSize: '13.5px',
                  fontFamily: 'monospace',
                  lineHeight: 1.6,
                  resize: 'vertical',
                  marginBottom: '16px',
                }}
              />
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={handleSaveEdits}
                  style={{ background: '#0284c7', color: '#ffffff', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: 'pointer' }}
                >
                  Save Edits
                </button>
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  style={{ background: '#090e1a', color: '#94a3b8', border: '1px solid #1e293b', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* ARTICLE READABLE PREVIEW CONTAINER (Requirement 8) */
            <div style={{ maxWidth: '850px', margin: '0 auto', background: '#090e1a', border: '1px solid #1e293b', borderRadius: '12px', padding: '36px 40px', color: '#cbd5e1', fontSize: '14.5px', lineHeight: 1.7 }}>
              {selectedPage.generatedContent.split('\n').map((line, lineIdx) => {
                const trimmed = line.trim();
                if (!trimmed) return <div key={lineIdx} style={{ height: '12px' }} />;

                if (trimmed.startsWith('# ')) {
                  return (
                    <h1 key={lineIdx} style={{ fontSize: '26px', fontWeight: 900, color: '#ffffff', margin: '0 0 20px 0', borderBottom: '2px solid #0284c7', paddingBottom: '12px', lineHeight: 1.3 }}>
                      {trimmed.replace(/^#\s+/, '')}
                    </h1>
                  );
                }

                if (trimmed.startsWith('## ')) {
                  return (
                    <h2 key={lineIdx} style={{ fontSize: '20px', fontWeight: 800, color: '#38bdf8', marginTop: '28px', marginBottom: '14px', borderBottom: '1px solid #1e293b', paddingBottom: '8px', lineHeight: 1.4 }}>
                      {trimmed.replace(/^##\s+/, '')}
                    </h2>
                  );
                }

                if (trimmed.startsWith('### ')) {
                  return (
                    <h3 key={lineIdx} style={{ fontSize: '16px', fontWeight: 700, color: '#f59e0b', marginTop: '20px', marginBottom: '10px', lineHeight: 1.4 }}>
                      {trimmed.replace(/^###\s+/, '')}
                    </h3>
                  );
                }

                if (trimmed.startsWith('- ')) {
                  return (
                    <li key={lineIdx} style={{ marginLeft: '20px', marginBottom: '6px', color: '#e2e8f0' }}>
                      {trimmed.replace(/^- /, '')}
                    </li>
                  );
                }

                return (
                  <p key={lineIdx} style={{ margin: '0 0 14px 0', color: '#cbd5e1' }}>
                    {trimmed}
                  </p>
                );
              })}
            </div>
          )}
        </div>
      ) : (
        /* Empty State before generation */
        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '16px', padding: '40px 24px', textAlign: 'center', marginBottom: '24px' }}>
          <Sparkles size={36} color="#334155" style={{ margin: '0 auto 12px auto' }} />
          <h4 style={{ fontSize: '16px', fontWeight: 700, color: '#ffffff', margin: '0 0 6px 0' }}>
            Ready to Generate Webpage Content
          </h4>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Click <strong>Generate Content</strong> above to build the publication-ready webpage article using your Step 3 outline.
          </p>
        </div>
      )}

      {/* 9. SEO INFORMATION PANEL (COLLAPSIBLE) (Requirement 9) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', marginBottom: '20px', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setIsSeoPanelExpanded(!isSeoPanelExpanded)}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: '#070c16',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Globe size={16} color="#10b981" />
            <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
              SEO Metadata & Strategy Info
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
            <span>{isSeoPanelExpanded ? 'Hide Info' : 'View Info'}</span>
            {isSeoPanelExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>

        {isSeoPanelExpanded && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #1e293b', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>SEO Title</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#ffffff', background: '#090e1a', border: '1px solid #1e293b', padding: '10px', borderRadius: '8px' }}>
                {outline.seoTitle}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Meta Description</div>
              <div style={{ fontSize: '12.5px', color: '#cbd5e1', background: '#090e1a', border: '1px solid #1e293b', padding: '10px', borderRadius: '8px', lineHeight: 1.4 }}>
                {outline.metaDescription}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>H1 Heading</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', background: '#090e1a', border: '1px solid #1e293b', padding: '10px', borderRadius: '8px' }}>
                {outline.h1}
              </div>
            </div>

            <div>
              <div style={{ fontSize: '11px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase', marginBottom: '4px' }}>Recommended Length</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#10b981', background: '#090e1a', border: '1px solid #1e293b', padding: '10px', borderRadius: '8px' }}>
                ~{outline.recommendedWordCount || 2500} words
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 10. CONTENT INPUTS / SOURCE CONTEXT (Requirement 10) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', marginBottom: '20px', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setIsInputsExpanded(!isInputsExpanded)}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: '#070c16',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Info size={16} color="#f59e0b" />
            <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
              Content Inputs & Source Context
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
            <span>{isInputsExpanded ? 'Hide Context' : 'View Context'}</span>
            {isInputsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>

        {isInputsExpanded && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #1e293b', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>PRIMARY KEYWORD</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b', marginTop: '4px' }}>{selectedPage.primaryKeyword || 'Not set'}</div>
            </div>

            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>COMPETITORS ANALYZED</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>{compHeadingsList.length} Competitors</div>
            </div>

            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>PAIN POINTS LOGGED</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#a855f7', marginTop: '4px' }}>{painPointsList.length} Pain Points</div>
            </div>

            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>CONTENT GAPS</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#10b981', marginTop: '4px' }}>{(outline.contentGaps || []).length} Identified</div>
            </div>

            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>VALUE ADDITIONS</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#38bdf8', marginTop: '4px' }}>{(outline.valueAdditions || []).length} Value Adds</div>
            </div>

            <div style={{ background: '#090e1a', border: '1px solid #1e293b', padding: '12px', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 800 }}>STRATEGY FAQS</div>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#f59e0b', marginTop: '4px' }}>{(outline.aeoFaqs || []).length} FAQs</div>
            </div>
          </div>
        )}
      </div>

      {/* 11. STRATEGY FAQS (COLLAPSIBLE) (Requirement 11) */}
      {(outline.aeoFaqs || []).length > 0 && (
        <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', marginBottom: '20px', overflow: 'hidden' }}>
          <button
            type="button"
            onClick={() => setIsFaqsExpanded(!isFaqsExpanded)}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: '#070c16',
              border: 'none',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <HelpCircle size={16} color="#a855f7" />
              <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
                Strategy FAQs & AEO Questions ({outline.aeoFaqs.length})
              </span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
              <span>{isFaqsExpanded ? 'Hide FAQs' : 'View FAQs'}</span>
              {isFaqsExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </button>

          {isFaqsExpanded && (
            <div style={{ padding: '20px 24px', borderTop: '1px solid #1e293b', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {outline.aeoFaqs.map((faq, idx) => (
                <div key={faq.id || idx} style={{ background: '#090e1a', border: '1px solid #1e293b', borderRadius: '8px', padding: '12px 16px' }}>
                  <div style={{ fontSize: '13.5px', fontWeight: 700, color: '#f59e0b', marginBottom: '4px' }}>
                    Q: {faq.question}
                  </div>
                  {faq.answer && (
                    <div style={{ fontSize: '12.5px', color: '#cbd5e1', lineHeight: 1.5 }}>
                      A: {faq.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 14. RAW OUTPUT (COLLAPSIBLE) (Requirement 14) */}
      <div style={{ background: '#070c16', border: '1px solid #1e293b', borderRadius: '14px', overflow: 'hidden' }}>
        <button
          type="button"
          onClick={() => setIsRawOutputExpanded(!isRawOutputExpanded)}
          style={{
            width: '100%',
            padding: '16px 24px',
            background: '#070c16',
            border: 'none',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            cursor: 'pointer',
            textAlign: 'left',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <FileCode size={16} color="#64748b" />
            <span style={{ fontSize: '14px', fontWeight: 800, color: '#ffffff' }}>
              View Raw Output
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#94a3b8' }}>
            <span>{isRawOutputExpanded ? 'Hide Raw' : 'View Raw'}</span>
            {isRawOutputExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </div>
        </button>

        {isRawOutputExpanded && (
          <div style={{ padding: '20px 24px', borderTop: '1px solid #1e293b' }}>
            <textarea
              readOnly
              rows={12}
              value={selectedPage.generatedContent || 'No content generated yet.'}
              style={{
                width: '100%',
                padding: '14px',
                borderRadius: '10px',
                background: '#090e1a',
                border: '1px solid #1e293b',
                color: '#94a3b8',
                fontSize: '12px',
                fontFamily: 'monospace',
                lineHeight: 1.5,
                resize: 'vertical',
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
